import { $getSelection, $isRangeSelection } from "lexical";
import { StyledTextNode } from "../plugins/StyledTextNode";
import { $patchStyleText } from "@lexical/selection";

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export { isValidEmail };

// ─── Style Utilities ──────────────────────────────────────────────────────────

// "color: red; font-size: 14px" → { color: "red", "font-size": "14px" }
export function styleStringToMap(style: string): Record<string, string> {
  return Object.fromEntries(
    style
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        const [prop, ...rest] = s.split(":");
        return [prop.trim(), rest.join(":").trim()];
      }),
  );
}

// { color: "red", "font-size": "14px" } → "color: red; font-size: 14px"
export function mapToStyleString(map: Record<string, string>): string {
  return Object.entries(map)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k}: ${v}`)
    .join("; ");
}

// Merge patch into an existing style string — null value removes the property
export function mergeStyles(
  existing: string,
  patch: Record<string, string | null>,
): string {
  const map = styleStringToMap(existing);
  for (const [prop, value] of Object.entries(patch)) {
    if (value === null) delete map[prop];
    else map[prop] = value;
  }
  return mapToStyleString(map);
}

// Extract inline styles from a DOM element, stripping properties Lexical
// adds on export that are meaningless on import (white-space: pre-wrap)
export function extractStyles(el: HTMLElement): string {
  const map = styleStringToMap(el.getAttribute("style") ?? "");
  // Remove Lexical export artifacts that shouldn't influence re-import
  delete map["white-space"];
  return mapToStyleString(map);
}

// Helper: patch style on all StyledTextNodes in selection
export function $patchStyledNodes(patch: Record<string, string | null>): void {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return;

  const nodes = selection.getNodes();
  nodes.forEach((node) => {
    if (node instanceof StyledTextNode) {
      node.patchStyle(patch); // merges, doesn't replace
    } else {
      // fallback for regular TextNodes — use Lexical's built-in
      if (Object.keys(patch).length) {
        $patchStyleText(selection, patch);
      }
    }
  });
}
